import styled, { keyframes } from 'styled-components';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import GitHubIcon from '@mui/icons-material/GitHub';
import { Bio } from '../../data/constants';

const pulse = keyframes`
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
`;

const FooterContainer = styled.footer`
  width: 100%;
  padding: 3rem 0 2rem;
  display: flex;
  justify-content: center;
  background: ${({ theme }) => theme.card};
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: ${({ theme }) => theme.gradient_primary};
    animation: ${pulse} 3s ease-in-out infinite;
  }
`;

const FooterWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
  padding: 1rem 2rem;
  color: ${({ theme }) => theme.text_primary};
`;

const Logo = styled.h2`
  font-weight: 700;
  font-size: 28px;
  background: ${({ theme }) => theme.gradient_text};
  background-size: 200% 100%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: 1px;
`;

const Nav = styled.nav`
  width: 100%;
  max-width: 800px;
  margin-top: 0.5rem;
  display: flex;
  flex-direction: row;
  gap: 2rem;
  justify-content: center;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 1.5rem;
    font-size: 14px;
  }
`;

const NavLink = styled.a`
  color: ${({ theme }) => theme.text_secondary};
  text-decoration: none;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.3s ease;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    bottom: -4px;
    left: 50%;
    background: ${({ theme }) => theme.gradient_primary};
    transition: all 0.3s ease;
    transform: translateX(-50%);
    border-radius: 2px;
  }
  
  &:hover {
    color: ${({ theme }) => theme.primary};
    text-shadow: 0 0 10px ${({ theme }) => theme.shadow_primary};
  }
  
  &:hover::after {
    width: 100%;
  }
`;

const SocialMediaIcons = styled.div`
  display: flex;
  margin-top: 1.5rem;
  gap: 16px;
`;

const SocialMediaIcon = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: ${({ theme }) => theme.text_secondary};
  transition: all 0.4s ease;
  padding: 12px;
  border-radius: 50%;
  background: ${({ theme }) => theme.glass};
  border: 1px solid ${({ theme }) => theme.glass_border};
  
  &:hover {
    color: ${({ theme }) => theme.bg};
    background: ${({ theme }) => theme.gradient_primary};
    border-color: transparent;
    transform: translateY(-6px) scale(1.1);
    box-shadow: 0 15px 30px ${({ theme }) => theme.shadow_primary};
  }
`;

const Divider = styled.div`
  width: 100%;
  max-width: 600px;
  height: 1px;
  background: ${({ theme }) => theme.glass_border};
  margin: 1rem 0;
`;

const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 0.5rem;
`;

const Copyright = styled.p`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.text_secondary};
  text-align: center;
`;

const MadeWith = styled.p`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.text_secondary};
  display: flex;
  align-items: center;
  gap: 6px;
  
  span {
    color: ${({ theme }) => theme.primary};
    font-family: 'JetBrains Mono', monospace;
  }
`;

function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <FooterContainer>
      <FooterWrapper>
        <Logo>Sunny Mondal</Logo>
        <Nav>
          <NavLink href="#about">About</NavLink>
          <NavLink href="#skills">Skills</NavLink>
          <NavLink href="#experience">Experience</NavLink>
          <NavLink href="#projects">Projects</NavLink>
          <NavLink href="#education">Education</NavLink>
          <NavLink href="#contact">Contact</NavLink>
        </Nav>
        <SocialMediaIcons>
          <SocialMediaIcon href={Bio.github} target="_blank" aria-label="GitHub">
            <GitHubIcon />
          </SocialMediaIcon>
          <SocialMediaIcon href={Bio.linkedin} target="_blank" aria-label="LinkedIn">
            <LinkedInIcon />
          </SocialMediaIcon>
          <SocialMediaIcon href={Bio.twitter} target="_blank" aria-label="Twitter">
            <TwitterIcon />
          </SocialMediaIcon>
          <SocialMediaIcon href={Bio.insta} target="_blank" aria-label="Instagram">
            <InstagramIcon />
          </SocialMediaIcon>
          <SocialMediaIcon href={Bio.facebook} target="_blank" aria-label="Facebook">
            <FacebookIcon />
          </SocialMediaIcon>
        </SocialMediaIcons>
        <Divider />
        <BottomSection>
          <Copyright>
            &copy; {currentYear} Sunny Mondal. All rights reserved.
          </Copyright>
          <MadeWith>
            Built with <span>React</span> & <span>💜</span>
          </MadeWith>
        </BottomSection>
      </FooterWrapper>
    </FooterContainer>
  );
}

export default Footer;